import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

const VARIANT_STYLES = {
  default: {
    table: 'min-w-full text-left text-sm',
    thead: 'bg-surface-muted',
    th: 'px-3 py-2 text-left font-semibold text-content-secondary',
    td: 'px-3 py-2 align-middle text-content',
    tbody: 'divide-y divide-edge',
  },
  dense: {
    table: 'min-w-full text-left text-sm',
    thead: 'bg-surface-muted',
    th: 'px-2 py-1.5 text-left font-semibold text-content-secondary',
    td: 'px-2 py-1.5 align-middle text-content',
    tbody: 'divide-y divide-edge',
  },
};

/**
 * Sortable grid with chevron controls. For dense list-style tables, use {@link DataTable}.
 *
 * @param {object} props
 * @param {'default'|'dense'} [props.variant='default']
 * @param {Array<{
 *   key: string,
 *   label: import('react').ReactNode,
 *   sortable?: boolean,
 *   align?: 'left'|'right'|'center',
 *   width?: string,
 *   render?: (value: unknown, row: object) => import('react').ReactNode,
 * }>} props.columns
 * @param {object[]} props.data
 * @param {string} [props.defaultSortKey]
 * @param {'asc'|'desc'} [props.defaultSortDirection='asc']
 * @param {(row: object) => void} [props.onRowClick]
 * @param {string} [props.emptyMessage='No data to display.']
 * @param {string} [props.caption]
 */
const Table = ({
  columns,
  data = [],
  variant = 'default',
  defaultSortKey,
  defaultSortDirection = 'asc',
  onRowClick,
  emptyMessage = 'No data to display.',
  caption,
}) => {
  const v = VARIANT_STYLES[variant] ?? VARIANT_STYLES.default;
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return data;
    const sorted = [...data].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return av - bv;
      }
      return String(av).localeCompare(String(bv));
    });
    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [columns, data, sortKey, sortDirection]);

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
      return;
    }
    setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  const rowClickable = typeof onRowClick === 'function';

  return (
    <div className="overflow-x-auto">
      <table className={v.table}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className={v.thead}>
          <tr>
            {columns.map((col) => {
              const isSorted = sortKey === col.key;
              const sortable = col.sortable === true;
              const align = col.align ?? 'left';
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={[
                    v.th,
                    align === 'right' ? 'text-right' : '',
                    align === 'center' ? 'text-center' : '',
                    col.width ?? '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-sort={isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className="inline-flex items-center gap-1 rounded-sm hover:text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-edge-focus"
                    >
                      {col.label}
                      {isSorted ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp size={14} aria-hidden />
                        ) : (
                          <ChevronDown size={14} aria-hidden />
                        )
                      ) : (
                        <ChevronsUpDown size={14} aria-hidden className="opacity-50" />
                      )}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={v.tbody}>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={`${v.td} py-8 text-center text-content-faint`}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, rowIdx) => {
              const key = row.id != null ? String(row.id) : `row-${rowIdx}`;
              return (
                <tr
                  key={key}
                  className={rowClickable ? 'cursor-pointer hover:bg-surface-hover-subtle' : ''}
                  onClick={rowClickable ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => {
                    const raw = row[col.key];
                    const content = col.render ? col.render(raw, row) : raw;
                    const align = col.align ?? 'left';
                    return (
                      <td
                        key={col.key}
                        className={[
                          v.td,
                          align === 'right' ? 'text-right' : '',
                          align === 'center' ? 'text-center' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
